import { type QueryKey } from "@tanstack/react-query";
import { type RefObject, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useSurface, type SurfaceKey } from "@/contexts/surface-context";
import {
  flush,
  registerImpressionElement,
  statusTrackingTarget,
  unregisterImpressionElement,
  type TrackTarget,
} from "@/impressions";

interface TrackOverride {
  surface: SurfaceKey;
  positionQueryKey?: QueryKey | null; // defaults null (no feed position)
}

/**
 * Register a status's root DOM node for impression tracking. Surface + the served-list query key
 * come from the nearest <SurfaceProvider>, unless an explicit `override` is given (thread rows).
 * A status with neither is not tracked. The tracking engine lives in `@/impressions` — this hook
 * is only the React glue.
 */
const useStatusTracking = (
  ref: RefObject<HTMLElement | null> | null,
  target: TrackTarget | null,
  override?: TrackOverride,
): void => {
  const ambient = useSurface();

  const surface = override?.surface ?? ambient?.surface;
  const positionQueryKey = override
    ? (override.positionQueryKey ?? null)
    : (ambient?.positionQueryKey ?? null);

  useEffect(() => {
    const element = ref?.current;
    if (!element || !target || !surface) return;

    registerImpressionElement(element, { ...target, surface, positionQueryKey });

    return () => unregisterImpressionElement(element, target.itemId);
    // Payload fields are intrinsic per itemId (stable), so identity deps suffice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, target?.itemId, target?.statusId, surface, positionQueryKey]);
};

/**
 * Mount once in a persistent shell inside the Router. Flushes on route change so a brief
 * single-status visit isn't stranded in the queue. The previous route's statuses have already
 * unmounted (episodes force-closed + enqueued) by the time this runs.
 */
const useImpressionRouteFlush = (): void => {
  const { pathname } = useLocation();
  useEffect(() => {
    void flush();
  }, [pathname]);
};

export { useStatusTracking, useImpressionRouteFlush, statusTrackingTarget };
